<template>
    <div class="bg-white" v-hotkey="keymap">
      <div v-if="desktopPanes" class="full-height">
        <div class="container nikud-edit">
            <b-row no-gutters v-if="showWrongGenreNotification">
                <b-col cols="9">
                    <div class="wrong-genre-notification">
                        <p class="wrong-genre-notification-text"><span @click="showWrongGenreNotification = false"><i-times
                                class="ml-1"></i-times></span>
                            זהינו שאתה משתמש בטקסט <span>{{ correctGenreText }}</span>.
                            למעבר לנקדן ה<span>{{ correctGenreText }}</span>
                            <span @click="switchGenre" class="click-to-switch-genre pr-2">לחץ כאן</span>
                            <i-info-circle class="pr-2" v-b-popover.hover.bottom="wrongGenreToolTipText"></i-info-circle>
                        </p>
                    </div>
                </b-col>
            </b-row>
            <b-row no-gutters class="nikud-edit-window">
                <b-col cols="9" class="full-height">
                    <div class="results-side">
                        <nikud-results-container class="results" :modes-obj="modesObj"
                                                 :edit-nikud-props="editNikudProps"/>
                    </div>
                </b-col>
                <b-col class="full-height" cols="3">
                    <div class="controls-side">
                        <nikud-pane-container class="full-height" :modes-obj="modesObj"
                                              :edit-nikud-props="editNikudProps"/>
                    </div>
                </b-col>
            </b-row>
        </div>
        <b-modal v-model="showSwitchGenrePrompt"
                id="switch-genre-modal"
                size="sm"
                centered
                hide-header
                ok-title="עבור"
                cancel-title="ביטול"
                footer-class="switch-genre-footer"
                @ok="actuallySwitchGenre">
            <p class="text-center mb-0">במעבר לנקדן אחר, תאבד את <br/>הניקוד הקיים</p>
            <span class="text-center"><b-form-checkbox v-model="disableSwitchGenrePromptOption">אל תציג שוב</b-form-checkbox></span>
        </b-modal>
        <failed-validation-popup />
      </div>
      <div v-else class="results-side-mobile">
          <nikud-results-container :modes-obj="modesObj" @open-options="modesObj.multiPaneOpen = true" />
          <mobile-options-multi-pane :modes-obj="modesObj" />
      </div>
    </div>
</template>

<script>
    // @ is an alias to /src
    import NikudPaneContainer from '@/components/NikudPaneContainer.vue'
    import NikudResultsContainer from '@/components/NikudResultsContainer.vue'
    import nikudMixins from '@/mixins/nikudMixins'
    import serviceMixins from '@/mixins/serviceMixins'
    import navigationMixins from '@/mixins/navigationMixins'
    import resultsIntegrationMixins from '@/mixins/resultsIntegrationMixins'
    import specialNavigationMixins from '@/mixins/specialNavigationMixins'
    import {StateSaver, Actions} from '@/store/stateChanges'
    import {RunStates} from '@/store/runStates'
    import FailedValidationPopup from '@/components/FailedValidationPopup.vue'
    import wordMutatingMixins from '@/mixins/wordMutatingMixins'
    import {getLetterWithReversedDageshFromLetterArray} from '@/js/commonHebrew'
    import {SimpleVsProConfig} from '@/simpleVsProConfig.js'
    import {NakdanStyle} from '@/mobileVsDesktopConfig.js'
    import MobileOptionsMultiPane from '@/components/MobileOptionsMultiPane.vue'

    const wrongGenreDescription = 'נקדן מודרני: מבוסס על כללי הניקוד המקובלים כיום על ידי האקדמיה ללשון.\nנקדן רבני: מתחשב בכללי ניקוד ייחודיים שאינם תואמים את הכללים הנורמטיבים של העברית המודרנית.\nנקדן שירה: נשען על הסתברות של ניקוד עבור טקסטים שיריים.'
    const UP = -1
    const DOWN = 1

    export default {
        name: 'NikudEdit',
        components: {
            NikudResultsContainer,
            NikudPaneContainer,
            MobileOptionsMultiPane,
            FailedValidationPopup
        },
        props: ['globalModesObj'],
        data() {
            return {
                modesObj: {
                    globals: this.globalModesObj,
                    firstClickOrDoubleClickDone: false, // used only in the "Simple" Nakdan
                    multiPaneOpen: false, // used only in the "Mobile" style
                    showMorph: false, // needs to be in this obj for the "Mobile" style
                    letterAddingMode: false,
                    wordMutationCount: 0
                },
                genreHebrewNames: {
                    modern: 'מודרני',
                    rabbinic: 'רבני',
                    poetry: 'שירה'
                },
                correctGenre: null,
                showWrongGenreNotification: false,
                wrongGenreToolTipText: wrongGenreDescription,
                disableSwitchGenrePromptOption: false,
                showSwitchGenrePrompt: false,
                editNikudProps: {
                    workingIndices: null,
                    showEditNikud: false
                },
                keymap: {
                    'shift+i': () => {
                        if (this.canClearNikud && !(this.modalOpen())) this.clearNikudEffectiveImmediately(this.localCurrentWord, this.localCurrentWordIndex)
                    },
                    'ctrl+i': () => {
                        if (this.canClearNikud && !(this.modalOpen())) this.clearNikudEffectiveImmediately(this.localCurrentWord, this.localCurrentWordIndex)
                    },
                    'left': () => {
                        if (!(this.localLetterAddingMode) && !(this.modalOpen())) this.moveToNextWord()
                    },
                    'g': () => {
                        if (this.localNikudKeyboard === 'blind' && !(this.localLetterAddingMode) && !(this.modalOpen())) {
                            this.moveToNextWord()
                        }
                    },
                    'shift+/': () => {
                        if (!(SimpleVsProConfig.NO_KEYBOARD_NIKUD_AT_ALL)
                            && this.canToggleDagesh && !(this.modalOpen())) this.toggleDageshInFirstLetterEffectiveImmediately()
                    },
                    'ctrl+/': () => {
                        if (!(SimpleVsProConfig.NO_KEYBOARD_NIKUD_AT_ALL)
                            && this.canToggleDagesh && !(this.modalOpen())) this.toggleDageshInFirstLetterEffectiveImmediately()
                    },
                    'right': () => {
                        if (!(this.localLetterAddingMode) && !(this.modalOpen())) this.moveToPreviousWord()
                    },
                    'h': () => {
                        if (this.localNikudKeyboard === 'blind' && !(this.localLetterAddingMode) && !(this.modalOpen())) {
                            this.moveToPreviousWord()
                        }
                    },
                    'alt+shift+O': () => {
                        if (SimpleVsProConfig.USE_MULTIPLE_NIKUD_KEYBOARDS && !(this.modalOpen())) {
                            this.$store.commit('SET_NIKUD_KEYBOARD', 'old');
                            this.showChangedKeyboardMessage()
                        }
                    },
                    'shift+O': () => {
                        if (SimpleVsProConfig.USE_MULTIPLE_NIKUD_KEYBOARDS && !(this.modalOpen())) {
                            this.$store.commit('SET_NIKUD_KEYBOARD', 'new');
                            this.showChangedKeyboardMessage()
                        }
                    },
                    'shift+L': () => {
                        if (SimpleVsProConfig.USE_MULTIPLE_NIKUD_KEYBOARDS && !(this.modalOpen())) {
                            this.$store.commit('SET_NIKUD_KEYBOARD', 'old');
                            this.showChangedKeyboardMessage()
                        }
                    },
                    'shift+alt+B': () => {
                        if (SimpleVsProConfig.USE_MULTIPLE_NIKUD_KEYBOARDS && !(this.modalOpen())) {
                            this.$store.commit('SET_NIKUD_KEYBOARD', 'blind');
                            this.showChangedKeyboardMessage()
                        }
                    },
                    'shift+T': () => {
                        if (!(this.modalOpen())) {
                            this.$store.commit('SET_SHOW_AIMS', !(this.$store.state.postSubmissionOptions.showAims))
                        }
                    },
                    // 'ctrl+T': () => {
                    //     if (!(this.modalOpen())) {
                    //         this.$store.commit('SET_SHOW_AIMS', !(this.$store.state.postSubmissionOptions.showAims))
                    //     }
                    // },
                    // move up and down:
                    'shift+up': () => { 
                        if (!(SimpleVsProConfig.NAVIGATION_KEYS_FOR_WORDS_ONLY)) this.moveToPreviousLine
                    },
                    'shift+down': () => { 
                        if (!(SimpleVsProConfig.NAVIGATION_KEYS_FOR_WORDS_ONLY)) this.moveToNextLine
                    },
                    'ctrl+shift+up': () => { 
                        if (!(SimpleVsProConfig.NAVIGATION_KEYS_FOR_WORDS_ONLY)) this.moveToPreviousParagraph
                    },
                    'ctrl+shift+down': () => { 
                        if (!(SimpleVsProConfig.NAVIGATION_KEYS_FOR_WORDS_ONLY)) this.moveToNextParagraph
                    }
                }
            }
        },
        computed: {
            desktopPanes () {
                return NakdanStyle.Desktop
            },
            canClearNikud () {
                return !(this.localLetterAddingMode) && this.currentLetterNotSep
            },
            localEditNikudMode() {
                return this.editNikudProps.showEditNikud
            },
            genreClassificationComplete() {
                return this.$store.state.genreClassifyApiState === RunStates.COMPLETE
            },
            correctGenreText() {
                return this.correctGenre ? this.genreHebrewNames[this.correctGenre] : '[לא נבחר סגנון]'
            },
            canToggleDagesh: function () {
                return !(this.localLetterAddingMode) && this.currentLetterNotSep
            }
        },
        watch: {
            localLetterAddingMode(newLocalLetterAddingMode) {
                if (newLocalLetterAddingMode) {
                    document.title = 'נקדן מקצועי מבית דיקטה (הוספה)'
                } else if (this.editNikudProps.showEditNikud) {
                    document.title = 'נקדן מקצועי מבית דיקטה (עריכה)'
                } else document.title = 'נקדן מקצועי מבית דיקטה'
            },
            localEditNikudMode(newLocalEditNikudMode) {
                if (newLocalEditNikudMode) {
                    document.title = 'נקדן מקצועי מבית דיקטה (עריכה)'
                } else if (this.localLetterAddingMode) {
                    document.title = 'נקדן מקצועי מבית דיקטה (הוספה)'
                } else document.title = 'נקדן מקצועי מבית דיקטה'
            },
            genreClassificationComplete(newGenreClassificationComplete) {
                if (newGenreClassificationComplete) this.useGenreClassification()
            }
        },
        mixins: [
            nikudMixins, /* for the direct use of: localLetterAddingMode, localNikudKeyboard, localCurrentWordIndex,
                                localCurrentLetterIndex, localCurrentWord, currentLetterNotSep,
                                modalOpen(), and showChangedKeyboardMessage() */
            resultsIntegrationMixins, // for populateAllWorkingNikud() and clearNikudEffectiveImmediately()
            wordMutatingMixins, /* for the direct use of: rewriteWordOrWordsInWorkingArrayMethod() ...
                                ...& b/c (now) whoever uses resultsIntegrationMixins needs wordMutatingMixins too */
            navigationMixins, // scrollResultsWindow(), moveToNextWord(), moveToPreviousWord(), & moveToWordMethod()
            specialNavigationMixins, // for getAdjacentParagraphWordIndex() and moveToAdjacentLine()
            serviceMixins // for googleAnalyticsId and gtag()
        ],
        methods: {
            moveToNextParagraph () {
                const toMoveTo = this.getAdjacentParagraphWordIndex(DOWN)
                if (toMoveTo !== this.localCurrentWordIndex) this.moveToWordMethod(toMoveTo)
            },
            moveToPreviousParagraph () { // more precisely, the "most recent" paragraph beginning
                const toMoveTo = this.getAdjacentParagraphWordIndex(UP)
                if (toMoveTo !== this.localCurrentWordIndex) this.moveToWordMethod(toMoveTo)
            },
            moveToNextLine () {
                const whetherMoved = this.moveToAdjacentLine(DOWN)
                if (whetherMoved) this.scrollResultsWindow()
            },
            moveToPreviousLine () {
                const whetherMoved = this.moveToAdjacentLine(UP)
                if (whetherMoved) this.scrollResultsWindow()
            },
            toggleDageshInFirstLetterEffectiveImmediately () {
                var letterArray = this.localCurrentWord.standardLetterArray
                var currentLetter = 0
                letterArray[currentLetter] = getLetterWithReversedDageshFromLetterArray(letterArray, currentLetter)
                this.rewriteWordOrWordsInWorkingArrayMethod({
                    contextData: { wordIndex: this.localCurrentWordIndex, letterIndex: this.localCurrentLetterIndex }, // for undo
                    newWordContainer: {
                        index: this.localCurrentWordIndex, changes: {
                            altered: true,
                            choiceType: 'manual',
                            manualNikud: letterArray.join('')
                        }
                    }
                })
            },
            useGenreClassification() {
                return /* (temporarily deactivating)
                if (this.$store.state.originalDocument.identifiedGenre !== this.$store.state.preSubmissionOptions.chosenTextTypeName) {
                    this.correctGenre = this.$store.state.originalDocument.identifiedGenre
                    this.showWrongGenreNotification = true
                } */
            },
            switchGenre() {
                if (!(this.$store.state.postSubmissionOptions.disableSwitchGenrePrompt)) { 
                    this.showSwitchGenrePrompt = true 
                } else this.actuallySwitchGenre()
            },
            actuallySwitchGenre() {
                this.showSwitchGenrePrompt = false
                if (this.disableSwitchGenrePromptOption) this.$store.commit('SET_DISABLE_SWITCH_GENRE_PROMPT', true)
                this.$store.commit('STORE_ALL_FRONTEND_NIKUD_OPTIONS',
                    Object.assign(this.$store.state.preSubmissionOptions, {chosenTextTypeName: this.correctGenre}))
                this.$store.dispatch(Actions.RUN_NAKDAN)
                this.$router.push({name: 'text-prep'})
            }
        },
        mounted: function () {
            if(document.body.classList.contains('ios-toolbar-gone')) {
                 document.body.classList.remove('ios-toolbar-gone')
            }
            if(document.body.classList.contains('ios-main')) {
               document.body.classList.remove('ios-main')
            }
            this.disableSwitchGenrePromptOption = this.$store.state.postSubmissionOptions.disableSwitchGenrePrompt
            this.gtag('config', this.googleAnalyticsId, {"page_path": "nikudedit"})
            if (!(this.$store.state.allWorkingNikud.wordsArray.length)) {
                this.$store.dispatch(Actions.INITIALIZE_APPLICATION)
                if (this.$store.state.nakdanApiResponse.length) {
                    if (this.$store.state.allWorkingNikud.id !== this.$store.state.originalDocument.id) {
                        const whetherToMove = this.populateAllWorkingNikud()
                        if (whetherToMove) this.moveToWordMethod(0)
                    }
                } else this.$router.push({ name: 'text-prep' } )
            }
            if (this.genreClassificationComplete) this.useGenreClassification()
        },
        beforeDestroy() {
            StateSaver.saveState(this.$store.state)
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    /* Nikud-Edit-specific CSS goes here */
    .nikud-edit {
        height: 100%;
        flex-direction: column;
        display: flex;
    }
    .nikud-edit-window {
        height: 100%;
        position: relative;
        /*border: solid 1px #d8d8d8;*/
        border-radius: 4px;
    }

    .results-side, .controls-side {
        position: relative;
    }

    .controls-side {
        height: 100%;
        margin-right: 8px;
        padding: 4px;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .results-side {
        /* in the control side, the shadow is inside, but on the result side the shadow is outside */
        margin-top: 4px;
        height: calc(100% - 8px);
        background-color: #f6f6f6;
        box-shadow: 2px 2px 2px 0 rgba(0, 0, 0, 0.15);
        border: solid 1px #e3e3e3;
        border-radius: 4px;
    }

    .results-side-mobile {
        color: black;
        background-color: #f6f6f6;
        height: 100%;
    }

    .wrong-genre-notification, .opposite-wrong-genre-notification {
        height: 20px;
        flex: none;
    }

    .wrong-genre-notification {
        text-align: right;
    }

    .wrong-genre-notification {
        background-color: #f6f6f6;
        position: relative;
        height: 28px;
        border-radius: 2px;
        margin-bottom: 15px;
        padding: 4px 10px;
    }

    .wrong-genre-notification-text {

        font-size: 13px;
        margin-bottom: 0;
    }

    .click-to-switch-genre {
        color: #007ee5;
        cursor: pointer;
    }

    @media (min-width: 1140px) {

    }
</style>
